import axios from 'api/axiosConfig'
import i18n from '../i18n'
import type { AuthUser, AuthStoredData, LoginCredentials } from 'Types'
import showError from './ErrorNotification'
import showSuccess from './SuccessNotification'

export const login = async (credentials: LoginCredentials) => {
 try {
 const response = await axios.post('/auth/login', credentials)
 const data = response.data
 if (data?.status === 'success' && data?.data) {
 const ts = localStorage.getItem('tenant_slug')
 localStorage.setItem(
 'authUser',
 JSON.stringify({
 ...data.data,
 ...(ts ? { tenant_slug: ts } : {}),
 })
 )
 window.dispatchEvent(new Event('auth:updated'))

 showSuccess(data.message || i18n.t('Login successful'))
 return { status: true, data: data.data }
 } else {
 const errData = data?.errors as Record<string, string[]> | undefined
 const firstError = errData ? (Array.isArray(Object.values(errData)[0]) ? (Object.values(errData)[0] as string[])[0] : Object.values(errData)[0]) : null
 showError(firstError || data?.message || i18n.t('Failed to login'))
 return { status: false }
 }
 } catch (error: any) {
 const errData = error.response?.data?.errors as Record<string, string[]> | undefined
 const firstError = errData ? (Array.isArray(Object.values(errData)[0]) ? (Object.values(errData)[0] as string[])[0] : Object.values(errData)[0]) : null
 showError(firstError || error.response?.data?.message || i18n.t('Failed to login'))
 return { status: false }
 }
}

export const logout = async (): Promise<void> => {
 try {
 const userData = getUserData()
 if (userData && userData.token) {
 await axios.post('/auth/logout')
 showSuccess(i18n.t('Logout successful'))
 }
 } catch (_error) {
 } finally {
 try {
 const { clearSensitivePosCachesOnLogout } = await import('store/posStore')
 await clearSensitivePosCachesOnLogout()
 } catch {
 /* ignore cache clear failures */
 }
 localStorage.removeItem('authUser')
 window.dispatchEvent(new Event('auth:updated'))
 localStorage.removeItem('protection')
 }
}

/**
 * Re-fetch `/auth/me` and replace stored user (same token).
 * Use after mutating the signed-in user’s roles, branches, or other server-side auth fields.
 * @returns Hydrated `AuthUser` (with `token`) or `null` on failure / no session.
 */
export async function refreshAuthSession(): Promise<AuthUser | null> {
 const u = getUserData()
 if (!u?.token) return null
 try {
 const res = await axios.get<{ status?: string; data?: { user?: AuthUser } }>('/auth/me')
 const payload = res.data
 if (payload?.status !== 'success' || !payload?.data?.user) return null
 const nextUser = payload.data.user
 const rawPrev = localStorage.getItem('authUser')
 let tenantSlug: string | undefined
 if (rawPrev) {
 try {
 const prev = JSON.parse(rawPrev) as { tenant_slug?: string }
 tenantSlug = prev.tenant_slug
 } catch {
 /* ignore */
 }
 }
 if (!tenantSlug) {
 tenantSlug = localStorage.getItem('tenant_slug') || undefined
 }
 const merged: AuthUser = { ...nextUser, token: u.token }
 localStorage.setItem(
 'authUser',
 JSON.stringify({
 user: nextUser,
 token: u.token,
 ...(tenantSlug ? { tenant_slug: tenantSlug } : {}),
 })
 )
 window.dispatchEvent(new Event('auth:updated'))
 return merged
 } catch {
 return null
 }
}

export const getUserData = (): AuthUser | undefined => {
 const raw = localStorage.getItem('authUser')
 if (!raw) return undefined
 try {
 const parsed = JSON.parse(raw) as AuthStoredData & Partial<AuthUser>
 const merged: AuthUser =
 parsed.user && parsed.token
 ? { ...parsed.user, token: parsed.token }
 : (parsed as unknown as AuthUser)
 return merged
 } catch {
 return undefined
 }
}

export const isAdmin = (): boolean => {
 const user = getUserData()
 if (!user) return false
 if (user.is_super_admin) return true
 if (user.roles?.some((r) => r === 'owner' || r === 'Super Admin' || r === 'branch_manager')) return true
 return false
}

export const isCashier = (): boolean => {
 const user = getUserData()
 if (!user) return false
 return Boolean(user.roles?.some((r) => r === 'cashier' || r === 'Cashier'))
}

/** Waiter-only staff (not owner / admin / manager / partner). */
export const isPureWaiter = (): boolean => {
 const user = getUserData()
 if (!user?.roles?.length) return false
 const elevated = ['owner', 'Super Admin', 'branch_manager', 'partner', 'admin']
 if (user.roles.some((r) => elevated.includes(r))) return false
 return user.roles.includes('waiter')
}

export const isAuthenticated = (): boolean => {
 const user = getUserData()
 return !!user
}

export const canDelete = (): boolean => {
 return hasPermission('delete') || isAdmin()
}

export const canCreate = (): boolean => {
 return hasPermission('create') || isAdmin()
}

export const canEdit = (): boolean => {
 return hasPermission('edit') || isAdmin()
}

const UNRESTRICTED_ROLES = ['Super Admin', 'owner'] as const

export const hasPermission = (permission: string): boolean => {
 const user = getUserData()
 if (!user) return false
 if (user.is_super_admin) return true
 if (user.roles?.some((r) => UNRESTRICTED_ROLES.includes(r as (typeof UNRESTRICTED_ROLES)[number]))) {
 return true
 }
 return Boolean(user.permissions?.includes(permission))
}

export const canAccessMenu = (menuLabel: string): boolean => {
 return hasPermission('access_' + menuLabel.toLowerCase().replace(/\s+/g, '_')) || isAdmin()
}
